import React, { useEffect, useRef, useState } from 'react'
import { WithChildren } from '@viveaesthetics/elemy-shared-core'
import { atom, useSetAtom } from 'jotai'

import { useRouter } from '@/hooks/useRouter'
import { useAppContext } from '@/context/PreloadAppContext'

import { Header } from './Header'
import { Footer } from './Footer'
import {
  DefaultLayoutContentWrapper,
  DefaultLayoutWrapper,
  HEADER_HEIGHT
} from './styles'

export const scrollableLayoutContainerAtom = atom<HTMLDivElement | null>(null)

interface DefaultLayoutProps extends WithChildren {
  hasSubheader?: boolean
}

export const DefaultLayout = ({
  children,
  hasSubheader
}: DefaultLayoutProps) => {
  const scrollableContentRef = React.useRef<HTMLDivElement>(null)
  const [hasScrolled, setHasScrolled] = useState(false)
  const router = useRouter()
  const setScrollableLayoutContainer = useSetAtom(scrollableLayoutContainerAtom)
  const previousPath = useRef(`${router.basePath}${router.pathname}`)
  const { branding } = useAppContext()

  useEffect(() => {
    const handleRouteChange = (url: string) => {
      const currentPath = new URL(url, window.location.origin).pathname
      if (currentPath !== previousPath.current) {
        scrollableContentRef.current?.scrollTo(0, 0)
        previousPath.current = currentPath
      }
    }

    router.events.on('routeChangeComplete', handleRouteChange)

    // Cleanup the event listener on unmount
    return () => {
      router.events.off('routeChangeComplete', handleRouteChange)
    }
  }, [router.events])

  useEffect(() => {
    setScrollableLayoutContainer(scrollableContentRef.current)
  }, [setScrollableLayoutContainer])

  const handleScroll: React.UIEventHandler<HTMLDivElement> = scrollEvent => {
    setHasScrolled(scrollEvent.currentTarget.scrollTop > HEADER_HEIGHT * 10)
  }

  return (
    <DefaultLayoutWrapper
      ref={scrollableContentRef}
      onScroll={handleScroll}
      data-test-id="layout"
    >
      <Header hasStaticBackground={hasScrolled || hasSubheader} />
      <DefaultLayoutContentWrapper
        backgroundImage={branding.images.bg || ''}
        id="main"
      >
        {children}
      </DefaultLayoutContentWrapper>
      <Footer />
    </DefaultLayoutWrapper>
  )
}
